// =============================================================================
// FILE: packages/renderer/src/lib/laborAgent.ts
// PURPOSE: "Labor & Timeline Agent". Derives a Bill of Materials + labor
//   man-hours from live project counts, produces a Manning Report (crew size,
//   duration, daily burn) and an auto-scheduled task list (with critical path)
//   ready to drop straight into the Interactive Gantt Chart.
// =============================================================================

import type { DashboardStats } from '../hooks/useDashboard.js';
import type { GanttTask } from '../components/gantt/GanttChart.js';

const DAY_MS = 86_400_000;
const HOURS_PER_DAY = 8;
const WORK_DAYS_PER_WEEK = 5;

export type LaborPhase = 'rough_in' | 'equipment' | 'devices' | 'commission';

export interface LaborLineItem {
  id: string;
  task: string;
  qty: number;
  unit: string;
  hoursPerUnit: number;
  totalHours: number;
  phase: LaborPhase;
  critical: boolean;   // sits on the critical path
}

export interface ManningReport {
  totalHours: number;
  crewSize: number;
  hoursPerDay: number;
  durationDays: number;
  durationWeeks: number;
  dailyBurnHours: number;
}

export interface LaborResult {
  bom: LaborLineItem[];
  manning: ManningReport;
  tasks: GanttTask[];
  finishDate: number;      // ms timestamp
  criticalPath: string[];  // task labels, ordered
}

const PHASE_META: Record<LaborPhase, { label: string; color: string; order: number }> = {
  rough_in:   { label: 'Phase 1 — Rough-In & Pathways',       color: '#38BDF8', order: 0 },
  equipment:  { label: 'Phase 2 — Racks & Active Equipment',  color: '#25B5A8', order: 1 },
  devices:    { label: 'Phase 3 — Device Installation',       color: '#34D399', order: 2 },
  commission: { label: 'Phase 4 — Test & Commissioning',      color: '#A78BFA', order: 3 },
};

// Man-hours per unit — editable "Labor Database" (mirrors Parts Library rates).
export const LABOR_RATES = {
  cablePull:   0.85,  // per drop (pull + terminate + test)
  trayConduit: 0.12,  // per ft of pathway
  rackInstall: 4.0,   // per rack
  patchPanel:  1.5,   // per panel
  switchCfg:   2.5,   // per switch (install + config)
  deviceMount: 1.2,   // per device/camera/AP
  fiberSplice: 3.0,   // per fiber backbone run
  testCert:    0.15,  // per port (Fluke cert)
};

export function buildLabor(
  stats: DashboardStats | undefined,
  crewSize = 4,
  startDate: number = Date.now(),
): LaborResult {
  const hasData = !!stats && (stats.connections || stats.ports || stats.racks || stats.assets);
  const d = (hasData ? stats : SAMPLE_STATS) as DashboardStats;
  const drops   = d.connections || d.ports || 0;
  const pathway = Math.max(drops * 60, 0);            // ~60 ft avg per drop
  const racks   = d.racks || 0;
  const panels  = d.patchPanels || 0;
  const switches = d.switches || 0;
  const devices = d.assets || 0;
  const fiber   = d.fiberCables || 0;
  const ports   = d.ports || drops;

  const raw: Array<Omit<LaborLineItem, 'id' | 'totalHours'>> = [
    { task: 'Cable tray & conduit pathway',   qty: pathway,  unit: 'ft',    hoursPerUnit: LABOR_RATES.trayConduit, phase: 'rough_in',   critical: true  },
    { task: 'Cat6A cable pull & terminate',   qty: drops,    unit: 'drops', hoursPerUnit: LABOR_RATES.cablePull,   phase: 'rough_in',   critical: true  },
    { task: 'Equipment rack installation',    qty: racks,    unit: 'racks', hoursPerUnit: LABOR_RATES.rackInstall, phase: 'equipment',  critical: true  },
    { task: 'Patch panel mount & label',      qty: panels,   unit: 'each',  hoursPerUnit: LABOR_RATES.patchPanel,  phase: 'equipment',  critical: false },
    { task: 'Switch install & configuration', qty: switches, unit: 'each',  hoursPerUnit: LABOR_RATES.switchCfg,   phase: 'equipment',  critical: true  },
    { task: 'Fiber backbone splice & test',   qty: fiber,    unit: 'runs',  hoursPerUnit: LABOR_RATES.fiberSplice, phase: 'equipment',  critical: false },
    { task: 'Device / camera / AP mounting',  qty: devices,  unit: 'each',  hoursPerUnit: LABOR_RATES.deviceMount, phase: 'devices',    critical: false },
    { task: 'Fluke certification & test',     qty: ports,    unit: 'ports', hoursPerUnit: LABOR_RATES.testCert,    phase: 'commission', critical: true  },
  ];

  const bom: LaborLineItem[] = raw
    .filter((r) => r.qty > 0)
    .map((r, i) => ({ ...r, id: `lbr-${i}`, totalHours: Math.round(r.qty * r.hoursPerUnit) }));

  const totalHours = bom.reduce((s, l) => s + l.totalHours, 0);
  const crew = Math.max(1, crewSize);
  const dailyBurnHours = crew * HOURS_PER_DAY;
  const durationDays = Math.max(1, Math.ceil(totalHours / dailyBurnHours));

  const manning: ManningReport = {
    totalHours,
    crewSize: crew,
    hoursPerDay: HOURS_PER_DAY,
    durationDays,
    durationWeeks: Math.max(1, Math.round((durationDays / WORK_DAYS_PER_WEEK) * 10) / 10),
    dailyBurnHours,
  };

  // ── Auto-schedule: sequence phases, size each by its share of man-hours ──
  const phases = (Object.keys(PHASE_META) as LaborPhase[]).sort((a, b) => PHASE_META[a].order - PHASE_META[b].order);
  const tasks: GanttTask[] = [];
  let cursor = startDate;

  for (const phase of phases) {
    const items = bom.filter((b) => b.phase === phase);
    if (items.length === 0) continue;
    const phaseHours = items.reduce((s, l) => s + l.totalHours, 0);
    const phaseDays = Math.max(1, Math.ceil(phaseHours / dailyBurnHours));
    const phaseStart = cursor;
    const phaseEnd = phaseStart + phaseDays * DAY_MS;

    tasks.push({
      id: `phase-${phase}`, title: PHASE_META[phase].label, kind: 'phase',
      startAt: phaseStart, endAt: phaseEnd, progress: 0, color: PHASE_META[phase].color,
    });

    // Stagger the line items across the phase window.
    let sub = phaseStart;
    for (const it of items) {
      const itDays = Math.max(1, Math.ceil(it.totalHours / dailyBurnHours));
      const itEnd = Math.min(sub + itDays * DAY_MS, phaseEnd);
      tasks.push({
        id: it.id, title: `${it.task} (${it.totalHours} hrs)`, kind: 'task',
        startAt: sub, endAt: itEnd, progress: 0,
        color: it.critical ? '#F87171' : PHASE_META[phase].color,
      });
      sub = itEnd;
    }

    tasks.push({
      id: `ms-${phase}`, title: `${PHASE_META[phase].label.split('—')[1]?.trim() ?? phase} complete`,
      kind: 'milestone', startAt: phaseEnd, endAt: phaseEnd, progress: 0, color: '#FCD34D',
    });

    cursor = phaseEnd;
  }

  const criticalPath = bom.filter((b) => b.critical).map((b) => b.task);

  return { bom, manning, tasks, finishDate: cursor, criticalPath };
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// Representative counts so the agent is never empty in web-preview / no-project mode.
export const SAMPLE_STATS = {
  connections: 480, ports: 480, racks: 10, patchPanels: 20, switches: 12,
  assets: 142, fiberCables: 24,
} as unknown as DashboardStats;
